// src/pages/IntelligencePage.tsx
import React, { useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Brain, TrendingUp, AlertTriangle, Users, Search,
  Zap, Target, ShoppingBag, DollarSign, Building2,
  Tv, Smartphone, Globe, Mail, Phone, Activity,
} from "lucide-react";
import { clsx } from "clsx";
import { intentCategories, intentTopics } from "@/data/intent";
import { financialSignals, creditTiers, incomeRanges } from "@/data/financial";
import { identityGraphStats, deviceGraphSample, GRAPH_DEVICE_STATS } from "@/data/identityGraph";
import { LOYALTY_SUMMARIES, RETAILERS } from "@/data";
import type { IntentTopic } from "@/types";
import LiveInsights from "@/components/intelligence/LiveInsights";

type Tab = "live" | "intent" | "loyalty" | "sku" | "financial" | "b2b" | "crossdevice";

// ── Lapse risk members ────────────────────────────────────────
const LAPSE_MEMBERS = [
  { id: "M-1042", name: "Sarah K.",  lastPurchase: "94 days ago",  lapseScore: 87, intentTopic: "Competitor Research", intentScore: 82, retailer: "Midwest Hardware" },
  { id: "M-2891", name: "James R.",  lastPurchase: "102 days ago", lapseScore: 84, intentTopic: "Home Improvement",    intentScore: 71, retailer: "Midwest Hardware" },
  { id: "M-0374", name: "Maria L.",  lastPurchase: "88 days ago",  lapseScore: 81, intentTopic: "Competitor Research", intentScore: 79, retailer: "FreshMart Grocery" },
  { id: "M-5512", name: "David T.",  lastPurchase: "115 days ago", lapseScore: 79, intentTopic: "Grocery Delivery",    intentScore: 74, retailer: "FreshMart Grocery" },
  { id: "M-3308", name: "Emily W.",  lastPurchase: "91 days ago",  lapseScore: 76, intentTopic: "Competitor Apparel",  intentScore: 68, retailer: "StyleNow Apparel"  },
];

const SKUS = [
  { sku: "DW-20V-001", name: "DeWalt 20V Max Drill",       category: "Power Tools",     price: 149, affinity: 88, topics: ["Power Tools","Home Improvement"], trending: true  },
  { sku: "DW-CIRC-02", name: "DeWalt 7-1/4 Circular Saw", category: "Power Tools",     price: 189, affinity: 82, topics: ["Power Tools","Contractor Tools"],  trending: true  },
  { sku: "MH-PAINT-01",name: "ProGrade Paint Roller Kit",  category: "Paint",           price: 34,  affinity: 71, topics: ["Home Improvement","DIY"],          trending: false },
  { sku: "FM-ORG-001",  name: "Organic Valley Whole Milk", category: "Dairy",           price: 7,   affinity: 74, topics: ["Organic Food","Health & Wellness"], trending: true  },
  { sku: "SN-ATH-001",  name: "StyleNow FlexRun Leggings", category: "Athletic Wear",   price: 68,  affinity: 79, topics: ["Athletic Wear","Running"],         trending: true  },
];

const COMPETITIVE = [
  { competitor: "Home Depot",  intentShare: 34, ourShare: 28, delta: -6,  category: "Home Improvement" },
  { competitor: "Lowe's",      intentShare: 22, ourShare: 31, delta: +9,  category: "Home Improvement" },
  { competitor: "Whole Foods", intentShare: 18, ourShare: 24, delta: +6,  category: "Organic Food"     },
  { competitor: "Instacart",   intentShare: 28, ourShare: 19, delta: -9,  category: "Grocery Delivery" },
  { competitor: "Nike.com",    intentShare: 41, ourShare: 22, delta: -19, category: "Athletic Wear"    },
];

// ── B2B use cases ─────────────────────────────────────────────
const B2B_USE_CASES = [
  { title: "Decision-Maker Reach at Home",  desc: "Target 121M business executives on CTV and display outside work hours — avoiding inbox noise.", icon: Building2, color: "bg-navy/10 text-navy" },
  { title: "B2B Intent + Consumer Identity", desc: "Match B2B research signals to personal identity for account-based campaigns with person-level precision.", icon: Brain,     color: "bg-blue/10 text-blue" },
  { title: "B2C to B2B Cross-Sell",          desc: "Consumer researching commercial equipment? Flag for B2B outreach. 20.6B weekly B2B signals.", icon: Zap,       color: "bg-cyan/10 text-cyan"  },
  { title: "ABM at Scale",                   desc: "Combine company-level intent with executive home identity. Reach decision-makers across 8,305 B2B topics.", icon: Target,   color: "bg-emerald-50 text-emerald-600" },
];

// ── Device icon map ───────────────────────────────────────────
const DEVICE_ICONS: Record<string, React.ReactNode> = {
  person:         <Users size={16} className="text-white" />,
  email:          <Mail size={14} className="text-white" />,
  phone:          <Phone size={14} className="text-white" />,
  maid_android:   <Smartphone size={14} className="text-white" />,
  maid_ios:       <Smartphone size={14} className="text-white" />,
  ctv:            <Tv size={14} className="text-white" />,
  ip_residential: <Globe size={14} className="text-white" />,
  ip_mobile:      <Globe size={14} className="text-white" />,
};

const DEVICE_COLORS: Record<string, string> = {
  person:         "#1E2761",
  email:          "#3056D3",
  phone:          "#3056D3",
  maid_android:   "#0891B2",
  maid_ios:       "#0891B2",
  ctv:            "#1E2761",
  ip_residential: "#06b6d4",
  ip_mobile:      "#06b6d4",
};

function fmt(n: number): string {
  if (n >= 1_000_000_000_000) return `${(n/1_000_000_000_000).toFixed(1)}T`;
  if (n >= 1_000_000_000)     return `${(n/1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)         return `${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000)             return `${(n/1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

export default function IntelligencePage() {
  const [tab, setTab]                   = useState<Tab>("live");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTopic, setSelectedTopic]       = useState<IntentTopic | null>(null);
  const [selectedRetailer, setSelectedRetailer] = useState("org-mhw");
  const [search, setSearch]             = useState("");

  const retailerSummary = LOYALTY_SUMMARIES.find(s => s.retailerId === selectedRetailer)!;
  const retailer        = RETAILERS.find(r => r.id === selectedRetailer)!;

  const categories = ["All", ...intentCategories.map(c => c.category)];

  const filteredTopics = intentTopics.filter(t => {
    const matchCat    = selectedCategory === "All" || t.category === selectedCategory;
    const matchSearch = t.topic.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const trendData = selectedTopic
    ? Array.from({ length: 30 }, (_, i) => ({
        day: `D${i+1}`,
        value: Math.round(selectedTopic.count7d * (0.7 + Math.random() * 0.6) / 30),
      }))
    : [];

  // Credit tier pie data
  const creditPieData = creditTiers.map(t => ({ name: t.label, value: t.count, pct: t.pct }));
  const PIE_COLORS = ["#1E2761","#3056D3","#0891B2","#06b6d4","#64748B"];

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="page-header mb-0">
        <h1 className="page-title">Intelligence Console</h1>
        <p className="page-subtitle">Intent signals, loyalty enrichment, SKU affinity, financial intelligence, and B2B analytics</p>
      </div>

      {/* Retailer selector for Live Insights */}
      {tab === "live" && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-slate">Retailer:</span>
          <select className="input text-xs w-48" value={selectedRetailer} onChange={e => setSelectedRetailer(e.target.value)}>
            {RETAILERS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {([
          { id: "live",        label: "Live Insights",      icon: Zap         },
          { id: "intent",      label: "Intent Explorer",    icon: Brain       },
          { id: "loyalty",     label: "Loyalty",            icon: Users       },
          { id: "sku",         label: "SKU Mapper",         icon: ShoppingBag },
          { id: "financial",   label: "Financial Intel",    icon: DollarSign  },
          { id: "b2b",         label: "B2B Signals",        icon: Building2   },
          { id: "crossdevice", label: "Cross-Device Graph", icon: Tv          },
        ] as { id: Tab; label: string; icon: any }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={clsx("flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all duration-150 -mb-px whitespace-nowrap",
              tab === t.id ? "border-blue text-blue" : "border-transparent text-slate hover:text-navy")}>
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Live Insights ── */}
      {tab === "live" && <LiveInsights retailerId={selectedRetailer} />}

      {/* ── Intent Explorer ── */}
      {tab === "intent" && (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-4 space-y-3">
            <div className="card">
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
                  <input className="input pl-8 text-xs" placeholder="Search topics..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {["All", ...intentCategories.slice(0,5).map(c => c.category.split(" ")[0])].map((cat, i) => (
                  <button key={cat}
                    onClick={() => setSelectedCategory(i === 0 ? "All" : intentCategories[i-1].category)}
                    className={clsx("px-2 py-0.5 rounded-full text-2xs font-medium transition-all",
                      (i === 0 ? selectedCategory === "All" : selectedCategory === intentCategories[i-1].category)
                        ? "bg-blue text-white" : "bg-gray-100 text-slate hover:bg-gray-200")}>
                    {cat}
                  </button>
                ))}
              </div>
              <div className="space-y-1 max-h-[480px] overflow-y-auto">
                {filteredTopics.slice(0, 40).map(topic => (
                  <button key={topic.id} onClick={() => setSelectedTopic(topic === selectedTopic ? null : topic)}
                    className={clsx("w-full text-left px-3 py-2 rounded-lg transition-all duration-150",
                      selectedTopic?.id === topic.id ? "bg-blue/10 border border-blue/20" : "hover:bg-gray-50 border border-transparent")}>
                    <div className="flex items-center justify-between">
                      <p className={clsx("text-xs font-semibold truncate pr-2", selectedTopic?.id === topic.id ? "text-blue" : "text-gray-800")}>
                        {topic.topic}
                      </p>
                      <span className={clsx("text-2xs font-medium flex-shrink-0",
                        topic.trend === "rising" ? "text-emerald-600" : topic.trend === "declining" ? "text-red-500" : "text-slate")}>
                        {topic.trend === "rising" ? "↑" : topic.trend === "declining" ? "↓" : "→"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-2xs text-slate">{fmt(topic.count7d)} / 7d</span>
                      <span className="text-2xs text-slate">{topic.subcategory}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-span-8 space-y-4">
            {selectedTopic ? (
              <>
                <div className="card">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-base font-semibold text-navy">{selectedTopic.topic}</h3>
                      <p className="text-xs text-slate mt-0.5">{selectedTopic.category} · {selectedTopic.subcategory}</p>
                    </div>
                    <div className="flex gap-3">
                      {[
                        { label: "7-Day Volume",  value: fmt(selectedTopic.count7d)  },
                        { label: "30-Day Volume", value: fmt(selectedTopic.count30d) },
                        { label: "Trend",         value: selectedTopic.trend.charAt(0).toUpperCase() + selectedTopic.trend.slice(1) },
                      ].map((m,i) => (
                        <div key={i} className="text-center bg-gray-50 rounded-lg px-3 py-2">
                          <p className="text-sm font-bold text-navy tabular">{m.value}</p>
                          <p className="text-2xs text-slate">{m.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={160}>
                    <AreaChart data={trendData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                      <defs>
                        <linearGradient id="intentGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#3056D3" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#3056D3" stopOpacity={0}   />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} interval={4} />
                      <YAxis tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }} />
                      <Area type="monotone" dataKey="value" stroke="#3056D3" strokeWidth={2} fill="url(#intentGrad)" name="Daily Signals" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="card">
                    <p className="section-label">Audience Actions</p>
                    <div className="space-y-2">
                      <button className="btn-primary w-full btn-sm justify-start gap-2"><Users size={13} /> Build Audience ({fmt(selectedTopic.count7d)})</button>
                      <button className="btn-secondary w-full btn-sm justify-start gap-2"><Target size={13} /> Create Campaign</button>
                      <button className="btn-secondary w-full btn-sm justify-start gap-2"><Zap size={13} /> Add to Suppression</button>
                    </div>
                  </div>
                  <div className="card">
                    <p className="section-label">Signal Breakdown</p>
                    <div className="space-y-2">
                      {[
                        { label: "1-day surge",      value: Math.round(selectedTopic.count7d * 0.18) },
                        { label: "7-day active",     value: Math.round(selectedTopic.count7d * 0.52) },
                        { label: "30-day sustained", value: Math.round(selectedTopic.count30d * 0.30) },
                      ].map((s,i) => (
                        <div key={i} className="flex justify-between">
                          <span className="text-xs text-slate">{s.label}</span>
                          <span className="text-xs font-semibold text-navy tabular">{fmt(s.value)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between pt-1 border-t border-gray-100">
                        <span className="text-xs text-slate">IAB Code</span>
                        <span className="text-xs font-mono text-slate">{selectedTopic.iabCode}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="card flex flex-col items-center justify-center py-16">
                <Brain size={36} className="text-gray-200 mb-3" />
                <p className="text-sm font-medium text-slate">Select a topic to explore</p>
                <p className="text-xs text-gray-400 mt-1">8,305 topics across 25 categories · 1-day latency</p>
              </div>
            )}

            {/* Category summary */}
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">Top Categories by 7-Day Volume</h3>
              <div className="space-y-2">
                {intentCategories.slice(0, 6).map(cat => (
                  <div key={cat.category}>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs font-medium text-gray-800">{cat.category}</span>
                      <span className="text-xs text-slate tabular">{fmt(cat.volume7d)} · {cat.topics} topics</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full" style={{ width: `${(cat.volume7d / intentCategories[0].volume7d) * 100}%`, backgroundColor: cat.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Loyalty ── */}
      {tab === "loyalty" && (
        <div className="space-y-4">
          <div className="flex gap-2">
            {RETAILERS.map(r => (
              <button key={r.id} onClick={() => setSelectedRetailer(r.id)}
                className={clsx("btn btn-sm", selectedRetailer === r.id ? "btn-primary" : "btn-secondary")}>
                {r.name}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-5 gap-4">
            {[
              { label: "Total Members",      value: fmt(retailerSummary.totalMembers),      color: "text-navy"        },
              { label: "Enriched",           value: fmt(retailerSummary.enrichedMembers),   color: "text-blue"        },
              { label: "Lapse Risk",         value: fmt(retailerSummary.lapseRiskCount),    color: "text-red-500"     },
              { label: "Conquest Prospects", value: fmt(retailerSummary.conquestProspects), color: "text-emerald-600" },
              { label: "Cross-Sell Opps",    value: fmt(retailerSummary.crossSellOpps),     color: "text-cyan"        },
            ].map((kpi,i) => (
              <div key={i} className="kpi-card">
                <p className="kpi-label">{kpi.label}</p>
                <p className={clsx("text-2xl font-bold tabular", kpi.color)}>{kpi.value}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">Intent Overlays on Loyalty Members</h3>
              <div className="space-y-3">
                {retailerSummary.intentOverlays.map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs font-medium text-gray-800">{item.topic}</span>
                      <div className="flex gap-2">
                        <span className="text-xs text-slate tabular">{fmt(item.memberCount)}</span>
                        <span className={clsx("text-xs font-semibold tabular", item.topic.includes("Competitor") ? "text-red-500" : "text-blue")}>{item.avgScore}/100</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className={clsx("h-2 rounded-full", item.topic.includes("Competitor") ? "bg-red-400" : "bg-gradient-to-r from-blue to-cyan")}
                        style={{ width: `${item.avgScore}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">Lapse Risk Members</h3>
              <table className="table-base">
                <thead><tr><th>Member</th><th>Last Purchase</th><th>Risk</th><th>Intent</th></tr></thead>
                <tbody>
                  {LAPSE_MEMBERS.filter(m => m.retailer === retailer.name).slice(0,4).map(m => (
                    <tr key={m.id}>
                      <td><p className="font-medium text-gray-800 text-xs">{m.name}</p><p className="text-2xs text-slate">{m.id}</p></td>
                      <td className="text-xs text-slate">{m.lastPurchase}</td>
                      <td><span className="text-xs font-bold text-red-500 tabular">{m.lapseScore}</span></td>
                      <td><span className={clsx("badge text-2xs", m.intentTopic.includes("Competitor") ? "badge-red" : "badge-blue")}>{m.intentTopic.split(" ")[0]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── SKU Mapper ── */}
      {tab === "sku" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-navy">SKU Affinity Scores</h3>
              <span className="badge badge-cyan">{SKUS.length} products mapped</span>
            </div>
            <table className="table-base">
              <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Affinity</th><th>Trending</th></tr></thead>
              <tbody>
                {SKUS.map(sku => (
                  <tr key={sku.sku}>
                    <td><p className="font-medium text-gray-800 text-xs">{sku.name}</p><p className="text-2xs text-slate">{sku.sku}</p></td>
                    <td><span className="badge badge-slate text-2xs">{sku.category}</span></td>
                    <td className="tabular text-xs">${sku.price}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-12 bg-gray-100 rounded-full h-1.5">
                          <div className={clsx("h-1.5 rounded-full", sku.affinity >= 80 ? "bg-emerald-500" : "bg-blue")} style={{ width: `${sku.affinity}%` }} />
                        </div>
                        <span className="text-xs font-semibold tabular">{sku.affinity}</span>
                      </div>
                    </td>
                    <td>{sku.trending && <span className="badge badge-green text-2xs">Trending</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-4">
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">Competitive Intent Share</h3>
              <div className="space-y-3">
                {COMPETITIVE.map((c, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-800">{c.competitor}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-2xs text-slate">{c.category}</span>
                        <span className={clsx("text-xs font-bold tabular", c.delta > 0 ? "text-emerald-600" : "text-red-500")}>{c.delta > 0 ? "+" : ""}{c.delta}pp</span>
                      </div>
                    </div>
                    <div className="flex gap-1 h-2">
                      <div className="bg-blue rounded-l-full" style={{ width: `${c.ourShare}%` }} />
                      <div className="bg-gray-200 rounded-r-full flex-1" />
                    </div>
                    <div className="flex justify-between mt-0.5">
                      <span className="text-2xs text-blue">Us: {c.ourShare}%</span>
                      <span className="text-2xs text-slate">{c.competitor}: {c.intentShare}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Financial Intelligence ── */}
      {tab === "financial" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Financial Records",     value: fmt(identityGraphStats.financial?.total ?? 475_000_000), sub: "Individuals with financial attributes" },
              { label: "Propensity Signals",    value: "8",           sub: "Distinct financial intent signals"     },
              { label: "Auto Loan Intent",      value: "224.3M",      sub: "47.1% of identity graph"               },
            ].map((kpi,i) => (
              <div key={i} className="kpi-card">
                <p className="kpi-label">{kpi.label}</p>
                <p className="kpi-value">{kpi.value}</p>
                <p className="text-xs text-slate mt-0.5">{kpi.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Propensity signals bar chart */}
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">Propensity Signal Coverage</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={financialSignals.map(s => ({ name: s.name.replace(" Intent","").replace(" Response",""), coverage: Math.round(s.coverage/1_000_000) }))}
                  layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} unit="M" />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: "#64748B" }} axisLine={false} tickLine={false} width={110} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }} formatter={(v: number) => [`${v}M`, "Coverage"]} />
                  <Bar dataKey="coverage" fill="#3056D3" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Credit tier pie */}
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">Credit Tier Distribution</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={creditPieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" nameKey="name" label={({ name, pct }) => `${name}: ${(pct*100).toFixed(0)}%`} labelLine={false}>
                    {creditPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => [fmt(v), "Records"]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 justify-center mt-1">
                {creditTiers.map((t,i) => (
                  <div key={t.range} className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PIE_COLORS[i] }} />
                    <span className="text-2xs text-slate">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Signal cards */}
          <div className="card">
            <h3 className="text-sm font-semibold text-navy mb-3">Financial Propensity Signals — Use Cases</h3>
            <div className="grid grid-cols-2 gap-3">
              {financialSignals.slice(0, 4).map(sig => (
                <div key={sig.id} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-navy">{sig.name}</p>
                    <span className="badge badge-blue text-2xs">{fmt(sig.coverage)}</span>
                  </div>
                  <p className="text-xs text-slate leading-relaxed">{sig.audienceUseCase}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── B2B Signals ── */}
      {tab === "b2b" && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: "Weekly B2B Signals",    value: "20.6B",  sub: "Business intent topics"           },
              { label: "Executive at Home",     value: "121M",   sub: "Decision-makers in consumer graph"},
              { label: "B2B Topics",            value: "8,305+", sub: "Matched to B2B taxonomy"          },
              { label: "Combined Weekly",       value: "25.5B",  sub: "B2B + B2C combined signals"       },
            ].map((kpi,i) => (
              <div key={i} className="kpi-card">
                <p className="kpi-label">{kpi.label}</p>
                <p className="kpi-value">{kpi.value}</p>
                <p className="text-xs text-slate mt-0.5">{kpi.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-3">B2B Use Cases</h3>
              <div className="space-y-3">
                {B2B_USE_CASES.map((uc, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className={clsx("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", uc.color)}>
                      <uc.icon size={15} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-navy">{uc.title}</p>
                      <p className="text-xs text-slate mt-0.5 leading-relaxed">{uc.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="card">
                <h3 className="text-sm font-semibold text-navy mb-3">Executive at Home — Targeting</h3>
                <div className="space-y-2">
                  {[
                    { label: "Total executives",          value: "121M"  },
                    { label: "Linked to consumer ID",     value: "121M"  },
                    { label: "CTV reachable",             value: "89M"   },
                    { label: "Mobile reachable",          value: "104M"  },
                    { label: "Match to B2B company data", value: "78M"   },
                  ].map((item,i) => (
                    <div key={i} className="flex justify-between items-center py-1.5 border-b border-gray-50 last:border-0">
                      <span className="text-xs text-slate">{item.label}</span>
                      <span className="text-xs font-bold text-navy tabular">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card bg-navy/5 border border-navy/10">
                <div className="flex items-center gap-2 mb-2">
                  <Zap size={14} className="text-cyan" />
                  <p className="text-xs font-semibold text-navy">Competitive Advantage</p>
                </div>
                <p className="text-xs text-slate leading-relaxed">
                  MRN is the only platform combining 20.6B weekly B2B signals with 121M executive consumer identities — enabling true person-level B2B targeting on CTV, display, and O&O.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Cross-Device Graph ── */}
      {tab === "crossdevice" && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: "Individuals",    value: fmt(identityGraphStats.individuals),              sub: "Verified persons"    },
              { label: "Mobile Devices", value: fmt(identityGraphStats.mobileDevices.total),      sub: "MAIDs linked"        },
              { label: "CTV Devices",    value: fmt(identityGraphStats.ctv.linkedToIdentity),     sub: "IFAs linked (74.6%)" },
              { label: "IP Records",     value: fmt(identityGraphStats.ipAddresses.total),        sub: "Household + mobile"  },
            ].map((kpi,i) => (
              <div key={i} className="kpi-card">
                <p className="kpi-label">{kpi.label}</p>
                <p className="kpi-value">{kpi.value}</p>
                <p className="text-xs text-slate mt-0.5">{kpi.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Person node diagram */}
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-1">Person-Level Identity Graph</h3>
              <p className="text-xs text-slate mb-4">Sample: {deviceGraphSample.personId} — all linked identifiers</p>

              {/* Visual node map */}
              <div className="relative flex items-center justify-center py-6">
                {/* Center person node */}
                <div className="absolute z-10 w-14 h-14 rounded-full flex flex-col items-center justify-center shadow-lg"
                  style={{ backgroundColor: "#1E2761" }}>
                  <Users size={18} className="text-white" />
                  <span className="text-2xs text-white/80 mt-0.5">Person</span>
                </div>

                {/* Surrounding device nodes */}
                {[
                  { label: "Email",    icon: Mail,       color: "#3056D3", angle: 0   },
                  { label: "Phone",    icon: Phone,      color: "#3056D3", angle: 51  },
                  { label: "Android",  icon: Smartphone, color: "#0891B2", angle: 103 },
                  { label: "iOS",      icon: Smartphone, color: "#0891B2", angle: 154 },
                  { label: "CTV 1",    icon: Tv,         color: "#1E2761", angle: 206 },
                  { label: "CTV 2",    icon: Tv,         color: "#1E2761", angle: 257 },
                  { label: "Home IP",  icon: Globe,      color: "#06b6d4", angle: 309 },
                ].map((node, i) => {
                  const rad = (node.angle * Math.PI) / 180;
                  const x   = Math.cos(rad) * 90;
                  const y   = Math.sin(rad) * 90;
                  return (
                    <div key={i} className="absolute flex flex-col items-center"
                      style={{ transform: `translate(${x}px, ${y}px)` }}>
                      <div className="w-9 h-9 rounded-full flex items-center justify-center shadow-md"
                        style={{ backgroundColor: node.color }}>
                        <node.icon size={14} className="text-white" />
                      </div>
                      <span className="text-2xs text-slate mt-0.5 whitespace-nowrap">{node.label}</span>
                    </div>
                  );
                })}

                {/* Spacer to give the diagram height */}
                <div className="w-full" style={{ height: 220 }} />
              </div>

              <div className="mt-2 space-y-1.5">
                {deviceGraphSample.devices?.slice(0,5).map((d: any, i: number) => (
                  <div key={i} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: DEVICE_COLORS[d.type] ?? "#64748B" }}>
                        {DEVICE_ICONS[d.type] ?? <Activity size={10} className="text-white" />}
                      </div>
                      <span className="text-xs text-gray-700">{d.label ?? d.type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={clsx("badge text-2xs", d.linkType === "deterministic" ? "badge-green" : "badge-yellow")}>
                        {d.linkType ?? "deterministic"}
                      </span>
                      <span className="text-2xs text-slate tabular">{((d.confidence ?? 0.9) * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                )) ?? (
                  <p className="text-xs text-slate text-center py-2">Device list available via API</p>
                )}
              </div>
            </div>

            {/* Graph-wide stats */}
            <div className="space-y-4">
              <div className="card">
                <h3 className="text-sm font-semibold text-navy mb-3">Graph-Wide Device Statistics</h3>
                <div className="space-y-3">
                  {[
                    { label: "Individuals",       value: identityGraphStats.individuals,             color: "bg-navy"          },
                    { label: "Email IDs",          value: identityGraphStats.email.totalEmailIds,     color: "bg-blue"          },
                    { label: "Phone Numbers",      value: identityGraphStats.phone.total,             color: "bg-blue"          },
                    { label: "Mobile Devices",     value: identityGraphStats.mobileDevices.total,     color: "bg-cyan"          },
                    { label: "CTV IFAs (total)",   value: identityGraphStats.ctv.totalIFA,            color: "bg-navy"          },
                    { label: "CTV IFAs (linked)",  value: identityGraphStats.ctv.linkedToIdentity,    color: "bg-emerald-500"   },
                    { label: "IP Records",         value: identityGraphStats.ipAddresses.total,       color: "bg-cyan"          },
                  ].map((item, i) => (
                    <div key={i}>
                      <div className="flex justify-between mb-1">
                        <span className="text-xs text-gray-700">{item.label}</span>
                        <span className="text-xs font-bold text-navy tabular">{fmt(item.value)}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className={clsx("h-1.5 rounded-full", item.color)}
                          style={{ width: `${Math.min((item.value / identityGraphStats.ipAddresses.total) * 100, 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card bg-cyan/5 border border-cyan/20">
                <div className="flex items-center gap-2 mb-2">
                  <Tv size={14} className="text-cyan" />
                  <p className="text-xs font-semibold text-navy">CTV Identity Advantage</p>
                </div>
                <p className="text-xs text-slate leading-relaxed mb-2">
                  MRN links <span className="font-semibold text-navy">17.4B CTV IFAs</span> to verified person identities — enabling true person-level attribution from TV exposure to purchase conversion.
                </p>
                <div className="flex gap-2">
                  <div className="flex-1 bg-white rounded-lg p-2 text-center border border-cyan/20">
                    <p className="text-sm font-bold text-navy">74.6%</p>
                    <p className="text-2xs text-slate">IFA-to-person match</p>
                  </div>
                  <div className="flex-1 bg-white rounded-lg p-2 text-center border border-cyan/20">
                    <p className="text-sm font-bold text-navy">1.4x</p>
                    <p className="text-2xs text-slate">Avg screens/household</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}